const CART_FETCH_ERROR = 'Unable to load cart. Please try again.';
const CART_FETCH_RETRY_DELAYS = [350, 900, 1800];

function isHtmlResponse(response, text) {
  const contentType = response.headers.get('content-type') || '';
  return contentType.includes('text/html') || /^\s*<!doctype\s+html/i.test(text) || /^\s*<html[\s>]/i.test(text);
}

function getCartUrl() {
  const root = window.Shopify?.routes?.root || '/';
  return `${root.replace(/\/?$/, '/')}cart.js`;
}

async function fetchCart(attempt = 0) {
  try {
    const response = await fetch(getCartUrl(), {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    const responseText = await response.text();
    const invalidResponse = !response.ok || isHtmlResponse(response, responseText);

    if (!invalidResponse) {
      try {
        return JSON.parse(responseText);
      } catch {
        // A truncated/non-JSON response is retryable just like Shopify's HTML challenge response.
      }
    }
  } catch {
    // Network failures use the same bounded retry path.
  }

  const retryDelay = CART_FETCH_RETRY_DELAYS[attempt];
  if (retryDelay !== undefined) {
    await new Promise((resolve) => window.setTimeout(resolve, retryDelay));
    return fetchCart(attempt + 1);
  }

  throw new Error(CART_FETCH_ERROR);
}

export const CartCache = {
  data: null,
  time: 0,
  ttl: 5000,
  fetching: false,
  pending: [],

  async get() {
    const now = Date.now();

    if (this.data && (now - this.time) < this.ttl) {
      return Promise.resolve(this.data);
    }

    if (this.fetching) {
      return new Promise((resolve, reject) => {
        this.pending.push({ resolve, reject });
      });
    }

    this.fetching = true;
    return fetchCart()
      .then((cart) => {
        this.data = cart;
        this.time = now;
        this.fetching = false;
        this.pending.forEach(({ resolve }) => resolve(cart));
        this.pending = [];
        return cart;
      })
      .catch((err) => {
        this.fetching = false;
        this.pending.forEach(({ reject }) => reject(err));
        this.pending = [];
        throw err;
      });
  },

  invalidate() {
    this.data = null;
    this.time = 0;
  },

  set(cart) {
    this.data = cart;
    this.time = Date.now();
    this.pending.forEach(({ resolve }) => resolve(cart));
    this.pending = [];
    this.fetching = false;
  },
};

export function installCartCacheGlobals() {
  window.cartCache = CartCache;
  window.getCart = async function () {
    return await CartCache.get();
  };
  window.setCart = function (cart) {
    CartCache.set(cart);
  };
}
